import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, ImagePlus, Loader2, Plus, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BrandStudioShell from "@/components/brandStudio/BrandStudioShell";
import BrandStudioSignature from "@/components/brandStudio/BrandStudioSignature";
import PrivateReferenceImage from "@/components/masterOrder/PrivateReferenceImage";
import { Button } from "@/components/ui/button";
import { BRAND_STUDIO_ROUTES } from "@/lib/brandJourney.mjs";
import { getVisualDirectionWorkspace, listBrandReferences } from "@/lib/brandStudioApi";

const errorText = (error) => error?.response?.data?.detail || "Не удалось загрузить референсы.";

function ReferenceChoice({ reference, selected, onToggle }) {
  return (
    <button type="button" role="checkbox" aria-checked={selected} onClick={() => onToggle(reference.id)}
            aria-label={selected ? "Исключить референс из нового поиска" : "Использовать референс в новом поиске"}
            className={"group relative block min-w-0 cursor-pointer overflow-hidden rounded-xl border bg-white text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/60 " + (selected ? "border-[#8B6F4B] ring-1 ring-[#C7A574]/50" : "border-[#E7DAC5] hover:border-[#CDB18B]")}>
      <div className="aspect-[4/3] overflow-hidden bg-[#F5F0E8]">
        <PrivateReferenceImage reference={reference} className="block h-full w-full object-contain" />
      </div>
      <span className="flex min-h-10 items-center justify-between gap-2 px-3 py-2">
        <span className="min-w-0 truncate text-[10.5px] text-[#806B52]">{reference.note || "Визуальный референс"}</span>
        <span className={"grid h-5 w-5 shrink-0 place-items-center rounded-full border " + (selected ? "border-[#72553D] bg-[#72553D] text-white" : "border-[#D8C8AF] bg-[#FFFDF8] text-transparent")} aria-hidden="true"><Check size={12} /></span>
      </span>
    </button>
  );
}

export default function MONewVisualDirection() {
  const nav = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [references, setReferences] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [nextWorkspace, nextReferences] = await Promise.all([
        getVisualDirectionWorkspace(),
        listBrandReferences(),
      ]);
      setWorkspace(nextWorkspace || null);
      setReferences(nextReferences || []);
      setSelectedIds((current) => current.size ? current : new Set((nextReferences || []).map((item) => item.id)));
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const recentReferences = useMemo(() => [...references].slice(-8).reverse(), [references]);
  const visibleReferences = showAll ? references : recentReferences;
  const approvedDirection = workspace?.directions?.find((item) => workspace?.approval?.visual_direction_id === item.id);
  const currentFoundation = approvedDirection?.title || approvedDirection?.name || "BOTANICAL SIGNATURE";

  const toggleReference = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const beginAnalysis = () => {
    nav(BRAND_STUDIO_ROUTES.visualDirection, {
      state: {
        entry: "new-visual-direction",
        referenceIds: Array.from(selectedIds),
        preserveCurrentFoundation: true,
      },
    });
  };

  return (
    <BrandStudioShell title="Новый визуальный поиск">
      <main className="w-full min-w-0 max-w-6xl overflow-x-clip pb-16" data-testid="new-visual-direction-workspace">
        <button type="button" onClick={() => nav(BRAND_STUDIO_ROUTES.home)}
                className="mb-7 inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-[#D9C39F] bg-[#FFFDF8] px-3 text-[12.5px] text-[#4A3728] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50">
          <ArrowLeft size={14} /> В Brand Studio
        </button>

        {loading ? (
          <div className="grid min-h-[280px] place-items-center text-[13px] text-[#806B52]">
            <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Загружаем референсы…</span>
          </div>
        ) : error ? (
          <section className="max-w-xl py-10">
            <h1 className="text-[34px] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Не удалось открыть новый поиск</h1>
            <p className="mt-3 text-[13px] text-[#806B52]">{error}</p>
            <Button type="button" onClick={load} className="mt-5">Повторить</Button>
          </section>
        ) : (
          <>
            <header className="max-w-3xl">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8B6F4B]">НОВЫЙ ЦИКЛ ПОИСКА</div>
              <h1 className="mt-2 text-[38px] leading-[1.02] text-[#2A1D14] md:text-[52px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Создать новое визуальное направление</h1>
              <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-[#5B4326]">Референсы, анализ и новый поиск — отдельно от уже утверждённой системы бренда.</p>
            </header>

            <section className="mt-7 rounded-2xl border border-[#DCC6A6] bg-[#F2E5D2] p-5 sm:p-6" data-testid="current-brand-foundation-preserved">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#FFF8EE] text-[#72553D]"><Sparkles size={16} /></span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.17em] text-[#8B6F4B]">Текущая основа сохранена</div>
                  <h2 className="mt-1 text-[25px] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{currentFoundation}</h2>
                  <p className="mt-2 max-w-3xl text-[12.5px] leading-relaxed text-[#6D5842]">Новый поиск не изменит утверждённую основу автоматически. Старые направления, revisions и Visual DNA останутся в истории. Смена основы возможна только отдельным явным действием владельца.</p>
                </div>
              </div>
            </section>

            <section className="mt-9 min-w-0" data-testid="new-direction-references">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B6F4B]">ШАГ 1</div>
                  <h2 className="mt-1 text-[30px] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Мои референсы</h2>
                  <p className="mt-2 text-[12.5px] text-[#6D5842]">Выбери изображения, которые нужно использовать в новом поиске.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {references.length > 8 && <Button type="button" variant="outline" onClick={() => setShowAll((value) => !value)} className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728]">{showAll ? "Показать последние" : "Показать все"}</Button>}
                  <Button type="button" variant="outline" onClick={() => nav(BRAND_STUDIO_ROUTES.references, { state: { returnTo: BRAND_STUDIO_ROUTES.newVisualDirection } })} className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728]"><ImagePlus size={15} /> Добавить референсы</Button>
                </div>
              </div>

              {references.length ? (
                <>
                  <div className="mt-5 grid min-w-0 grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="new-direction-reference-grid">
                    {visibleReferences.map((reference) => <ReferenceChoice key={reference.id} reference={reference} selected={selectedIds.has(reference.id)} onToggle={toggleReference} />)}
                  </div>
                  <div className="mt-3 text-[11px] text-[#806B52]">{selectedIds.size} выбрано · {references.length} сохранено</div>
                </>
              ) : (
                <div className="mt-5 rounded-2xl border border-dashed border-[#D8C19E] bg-[#FFFDF8] p-6 text-center">
                  <ImagePlus size={21} className="mx-auto text-[#8B6F4B]" />
                  <p className="mt-3 text-[13px] text-[#5B4326]">Добавь хотя бы один референс для нового поиска.</p>
                </div>
              )}
            </section>

            <section className="mt-10 rounded-2xl border border-[#E4D5C0] bg-[#FFFDF8] p-5 sm:p-6" data-testid="new-direction-analysis-foundation">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B6F4B]">ДАЛЬШЕ</div>
              <h2 className="mt-1 text-[28px] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Новый цикл визуального поиска</h2>
              <div className="mt-4 grid gap-2 sm:grid-cols-5">
                {["Референсы", "Анализ", "3 направления", "Выбор", "Уточнение"].map((item, index) => (
                  <div key={item} className="rounded-xl bg-[#F7F1E7] px-3 py-3">
                    <div className="text-[9px] uppercase tracking-[0.12em] text-[#9A8263]">0{index + 1}</div>
                    <div className="mt-1 text-[11.5px] text-[#4A3728]">{item}</div>
                  </div>
                ))}
              </div>
              <p className="mt-4 max-w-3xl text-[12px] leading-relaxed text-[#806B52]">Дальнейшая работа продолжится в существующем workspace визуальных направлений с его selection, revisions, refinement и историей.</p>
              <Button type="button" onClick={beginAnalysis} disabled={!selectedIds.size}
                      className="mt-5 min-h-11 bg-[#6A4933] px-5 text-[#FFF8EE] hover:bg-[#76543C]"
                      data-testid="start-new-visual-analysis">
                <Plus size={15} /> Начать новый анализ
              </Button>
              <p className="mt-3 text-[10.5px] leading-relaxed text-[#927A5B]">Это действие только открывает существующий flow. AI-анализ не запускается без отдельного подтверждения владельца.</p>
            </section>
          </>
        )}

        <footer className="pt-10"><BrandStudioSignature /></footer>
      </main>
    </BrandStudioShell>
  );
}
